import React from "react";

const DocumentReferenceCard = ({
  typeText = "Unknown Document",
  status = "unknown",
  docStatus = "N/A",
  categories = [],
  date = "",
  description = "",
  decodedData = "",
  period = null,
  isDarkTheme = true,
}) => {
  const useResponsiveStyles = () => {
    return {
      isMobile: window.innerWidth < 600,
    };
  };

  const { isMobile } = useResponsiveStyles();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Generate styles based on current state
  const styles = createStyles(isMobile, isDarkTheme);

  return (
    <div className="card-container" style={styles.container}>
      <div className="card-header" style={styles.header}>
        <div style={styles.titleWrapper}>
          <div style={styles.titleIcon}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h5 style={styles.title}>{typeText}</h5>
        </div>
      </div>

      <div className="card-details" style={styles.detailsContainer}>
        {decodedData && (
          <div className="notes-section" style={styles.notesSection}>
            <div style={styles.notesHeader}>
              <span className="detail-label" style={styles.notesLabel}>
                Notes
              </span>
            </div>
            <div style={styles.notesContent}>{decodedData}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Extracted styles object with standardized spacing and typography
const createStyles = (isMobile, isDarkTheme) => ({
  // Container styles
  container: {
    backgroundColor: isDarkTheme ? "#433F81" : "#dfe0f7",
    borderRadius: "12px",
    padding: isMobile ? "12px" : "16px",
    color: isDarkTheme ? "white" : "#333",
    width: "100%",
    maxWidth: "1000px",
    boxShadow: isDarkTheme
      ? "0 8px 24px rgba(0,0,0,0.25)"
      : "0 4px 16px rgba(93, 95, 239, 0.12)",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },

  // Header styles
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: "6px",
    paddingBottom: "10px",
    borderBottom: `2px solid ${isDarkTheme ? "#5A5698" : "#c9caf0"}`,
  },

  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  title: {
    fontSize: isMobile ? "14px" : "15px",
    fontWeight: "600",
    margin: 0,
    color: isDarkTheme ? "white" : "#333",
    letterSpacing: "-0.02em",
  },

  // Status badges
  statusContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },

  statusBadge: {
    backgroundColor: isDarkTheme ? "#8D8ED5" : "#7879c7",
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "500",
    color: "white",
    textTransform: "capitalize",
  },

  docStatusBadge: {
    backgroundColor: isDarkTheme ? "#E8756A" : "#df6a5f",
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "500",
    color: "white",
    textTransform: "capitalize",
  },

  // Details section
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  detailRow: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "4px" : "8px",
  },

  descriptionRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  detailLabel: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "13px",
  },

  detailValue: {
    fontSize: "14px",
    fontWeight: "500",
    color: isDarkTheme ? "white" : "#333",
  },

  separator: {
    margin: "0 8px",
    display: isMobile ? "none" : "inline",
    color: isDarkTheme ? "white" : "#333",
  },

  // Notes section
  notesSection: {
    marginTop: "4px",
    padding: isMobile ? "10px" : "12px",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    borderRadius: "10px",
    borderLeft: `4px solid ${isDarkTheme ? "#8D8ED5" : "#7879c7"}`,
    boxShadow: isDarkTheme
      ? "0 2px 8px rgba(0,0,0,0.15)"
      : "0 2px 8px rgba(93, 95, 239, 0.08)",
  },

  notesHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  },

  notesIcon: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  notesLabel: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  notesContent: {
    fontSize: "13px",
    lineHeight: "1.5",
    color: isDarkTheme ? "#E8E8F5" : "#333",
    whiteSpace: "pre-wrap",
    fontWeight: "400",
  },
});

export default DocumentReferenceCard;
